<template>
  <div v-if="visible" class="login-modal-overlay" @click="handleClose">
    <div class="login-modal" @click.stop>
      <!-- Logo区域 -->
      <div class="modal-logo">
        <div class="logo-container">
          <img src="@/assets/pc-logo.png" alt="Jingsun Car" class="logo-car-img" />
        </div>
      </div>

      <!-- 标签切换 -->
      <div class="modal-tabs">
        <div 
          class="tab-item" 
          :class="{ active: showLogin }"
          @click="switchToLogin"
        >
          {{ $t('login_page.login_button_title') }}
        </div>
        <div 
          class="tab-item" 
          :class="{ active: showRegister }"
          @click="switchToRegister"
        >
          {{ $t('register_page.register_button_title') }}
        </div>
      </div>

      <!-- 登录表单 -->
      <div v-if="showLogin" class="modal-form">
        <input
          v-model="loginForm.email"
          type="text"
          :placeholder="$t('login_page.login_form_email')"
          class="form-input"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
        />
        <input
          v-model="loginForm.password"
          type="password"
          :placeholder="$t('login_page.login_form_password')"
          class="form-input"
          autocomplete="new-password"
          autocapitalize="off"
          autocorrect="off"
        />
        <button class="submit-btn" @click="handleLoginSubmit" :disabled="loginLoading">
          {{ $t('login_page.login_button_title') }}
        </button>
      </div>

      <!-- 注册表单 -->
      <div v-if="showRegister" class="modal-form">
        <input
          v-model="registerForm.whatsapp"
          :placeholder="$t('register_page.register_form_whatsapp')"
          class="form-input"
        />
        <input
          v-model="registerForm.email"
          type="text"
          :placeholder="$t('register_page.register_form_email')"
          class="form-input"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
        />
        <input
          v-model="registerForm.password"
          type="password"
          :placeholder="$t('register_page.register_form_password')"
          class="form-input"
          autocomplete="new-password"
          autocapitalize="off"
          autocorrect="off"
        />
        <div class="verify-code-wrapper">
          <input
            v-model="registerForm.verification_code"
            type="text"
            :placeholder="$t('register_page.register_form_code')"
            class="form-input verify-code-input"
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
          />
          <button 
            class="verify-code-btn" 
            @click="getVerifyCode"
            :disabled="countdown > 0"
          >
            {{ countdown > 0 ? `${countdown}s` : $t('register_page.register_code_button') }}
          </button>
        </div>
        <button class="submit-btn" @click="handleRegisterSubmit" :disabled="registerLoading">
          {{ $t('register_page.register_button_title') }}
        </button>
        <!-- 用户协议提示 -->
        <div class="user-agreement">
          {{ $t('register_page.register_xieyi_left') }}
          <span class="agreement-link" @click="viewAgreement">{{ $t('register_page.register_xieyi_ahref') }}</span>
          {{ $t('register_page.register_xieyi_right') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { setToken } from '@/utils/auth'

export default {
  name: 'LoginModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'login', // 'login' 或 'register'
      validator: (value) => ['login', 'register'].includes(value)
    }
  },
  data() {
    return {
      showLogin: true,
      showRegister: false,
      loginForm: {
        email: '',
        password: ''
      },
      registerForm: {
        email: '',
        password: '',
        whatsapp: '',
        verification_code: ''
      },
      loginLoading: false,
      registerLoading: false,
      countdown: 0,
      countdownTimer: null
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // 根据 mode 设置初始显示状态
        if (this.mode === 'register') {
          this.showRegister = true
          this.showLogin = false
        } else {
          this.showLogin = true
          this.showRegister = false
        }
      } else {
        // 关闭时重置表单
        this.resetForm()
      }
    },
    mode(newVal) {
      if (this.visible) {
        if (newVal === 'register') {
          this.showRegister = true
          this.showLogin = false
        } else {
          this.showLogin = true
          this.showRegister = false
        }
      }
    }
  },
  beforeDestroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  },
  methods: {
    switchToLogin() {
      this.showLogin = true
      this.showRegister = false
    },
    switchToRegister() {
      this.showRegister = true
      this.showLogin = false
    },
    handleClose() {
      this.$emit('close')
      this.resetForm()
    },
    resetForm() {
      this.loginForm = { email: '', password: '' }
      this.registerForm = {
        email: '',
        password: '',
        whatsapp: '',
        verification_code: ''
      }
    },
    async handleLoginSubmit() {
      if (!this.loginForm.email) {
        this.$toast(this.$t('login_page.login_form_email'))
        return
      }
      if (!this.loginForm.password) {
        this.$toast(this.$t('login_page.login_form_password'))
        return
      }

      this.loginLoading = true
      try {
        const res = await this.$http.post('/auth/login', {
          email: this.loginForm.email,
          password: this.loginForm.password
        })

        if (res.data && res.data.token) {
          setToken(res.data.token)
        }
        
        this.$toast(res.message || this.$t('login_page.login_success') || '登录成功')
        this.handleClose()
        this.$router.go(0)
      } catch (error) {
        console.error('登录失败:', error)
      } finally {
        this.loginLoading = false
      }
    },
    async handleRegisterSubmit() {
      if (!this.registerForm.whatsapp) {
        this.$toast(this.$t('register_page.register_form_whatsapp'))
        return
      }
      if (!this.registerForm.email) {
        this.$toast(this.$t('register_page.register_form_email'))
        return
      }
      if (!this.registerForm.password) {
        this.$toast(this.$t('register_page.register_form_password'))
        return
      }
      if (!this.registerForm.verification_code) {
        this.$toast(this.$t('register_page.register_form_code'))
        return
      }

      this.registerLoading = true
      try {
        const res = await this.$http.post('/auth/register', {
          email: this.registerForm.email,
          password: this.registerForm.password,
          whatsapp: this.registerForm.whatsapp,
          verification_code: this.registerForm.verification_code
        })

        if (res.data && res.data.token) {
          setToken(res.data.token)
        }
        
        this.$toast(res.message || this.$t('register_page.register_success') || '注册成功')
        this.handleClose()
		this.$router.push('Redirect')
        // this.$router.go(0)
      } catch (error) {
		console.error('注册失败:', error)
      } finally {
        this.registerLoading = false
      }
    },
    async getVerifyCode() {
      if (!this.registerForm.email) {
        this.$toast(this.$t('register_page.register_form_email'))
        return
      }

      try {
        const res = await this.$http.post('/auth/send_code', {
          email: this.registerForm.email
        })
        
        this.$toast(res.message || this.$t('register_page.register_code_sent') || '验证码已发送')
        this.countdown = 60
        this.countdownTimer = setInterval(() => {
          this.countdown--
          if (this.countdown <= 0) {
            clearInterval(this.countdownTimer)
            this.countdownTimer = null
          }
        }, 1000)
      } catch (error) {
        console.error('获取验证码失败:', error)
      }
    },
    viewAgreement() {
      this.handleClose()
      this.$router.push({ name: 'UserAgreement' })
    }
  }
}
</script>

<style scoped lang="less">
.login-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.login-modal {
  position: relative;
  width: 580px;
  background-color: #fff;
  border-radius: 26px;
  padding: 40px 50px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  .modal-logo {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;

    .logo-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      border-radius: 8px;
      .logo-car-img {
        width: 135px;
        height: 55px;
        object-fit: contain;
        margin-bottom: 10px;
      }

      .logo-text {
        display: flex;
        align-items: baseline;
        font-weight: 600;
        font-size: 20px;
        letter-spacing: 1px;

        .logo-text-jingsun {
          color: #2c7873;
        }

        .logo-text-car {
          color: #BA3D38;
        }
      }
    }
  }

  .modal-tabs {
    display: flex;
    justify-content: center;
    gap: 80px;
    margin-bottom: 30px;

    .tab-item {
      padding: 12px 8px;
      font-size: 20px;
      color: #999;
      cursor: pointer;
      position: relative;
      transition: color 0.3s;

      &:hover {
        color: #666;
      }

      &.active {
        color: #333;
        font-weight: 900;

        &::after {
          content: '';
          position: absolute;
          bottom: -5px;
          left: 0;
          right: 0;
          height: 4px;
          background-color: #BA3D38;
        }
      }
    }
  }

  .modal-form {
    display: flex;
    flex-direction: column;
    gap: 15px;
     .form-input {
        width: 100%;
        height: 50px;
        padding: 0 20px;
        border: 1px solid transparent;
        border-radius: 8px;
        background-color: #F1F1F1;
        font-size: 16px;
        color: #323233;
        outline: none;
        transition: all 0.3s;

        &::placeholder {
          color: #c8c9cc;
        }

        &:focus {
          background-color: #e8e8e8;
          border-color: #BA3D38;
        }

        &.error {
          border-color: #ff4d4f;
          background-color: #fff5f5;
        }
      }

    .verify-code-wrapper {
      display: flex;
      gap: 10px;

      .verify-code-input {
        flex: 1;
      }

      .verify-code-btn {
        flex-shrink: 0;
        height: 50px;
        padding: 0 20px;
        border: 1px solid #666;
        border-radius: 8px;
        background-color: #fff;
        color: #666;
        font-size: 14px;
        cursor: pointer;
        white-space: nowrap;
        transition: all 0.3s;

        &:hover:not(:disabled) {
          background-color: #f5f5f5;
        }

        &:disabled {
          color: #999;
          border-color: #ddd;
          cursor: not-allowed;
        }
      }
    }

    .submit-btn {
      width: 300px;
      margin: 0 auto;
      height: 60px;
      margin-top: 30px;
      border: none;
      border-radius: 12px;
      background-color: #BA3D38;
      color: #fff;
      font-size: 20px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.3s;

      &:hover:not(:disabled) {
        background-color: #a33530;
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }

    .user-agreement {
      text-align: center;
      color: #9E9E9E;
      font-size: 12px;
      margin-top: 15px;

      .agreement-link {
        color: #1989fa;
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}
</style>

